import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Truck, Pencil, Trash2, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const SERVICES = ["Commercial & Heavy-Duty Maintenance", "Passenger Vehicle", "Custom Build (Boat, ATV, UTV, Lawn)", "Other"];
const STATUSES = ["new", "contacted", "quoted", "closed"];
const STATUS_COLORS = {
  new: "text-yellow-400 bg-yellow-500/15",
  contacted: "text-blue-400 bg-blue-500/15",
  quoted: "text-green-400 bg-green-500/15",
  closed: "text-zinc-400 bg-zinc-700/40",
};
const inputCls = "bg-zinc-900 border border-zinc-800 px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-shadow duration-150 w-full";

function EditModal({ quote, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: quote.name || "", email: quote.email || "", phone: quote.phone || "",
    service_type: quote.service_type || SERVICES[0], vehicle_info: quote.vehicle_info || "",
    message: quote.message || "", status: quote.status || "new",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/quote-requests/${quote.id}`, form);
      toast.success("Quote request updated");
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center overflow-y-auto p-6" data-testid="quote-edit-modal">
      <div className="bg-zinc-950 border border-zinc-800 w-full max-w-xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h2 className="font-display text-xl font-bold uppercase">Edit Quote Request</h2>
          <button onClick={onClose} data-testid="quote-edit-close"><X className="text-zinc-400 hover:text-white" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-xs uppercase text-zinc-400 block mb-1">Name</label><input value={form.name} onChange={set("name")} className={inputCls} data-testid="quote-edit-name" /></div>
            <div><label className="text-xs uppercase text-zinc-400 block mb-1">Phone</label><input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="quote-edit-phone" /></div>
          </div>
          <div><label className="text-xs uppercase text-zinc-400 block mb-1">Email</label><input type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="quote-edit-email" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-xs uppercase text-zinc-400 block mb-1">Service</label>
              <select value={form.service_type} onChange={set("service_type")} className={inputCls} data-testid="quote-edit-service">
                {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                {!SERVICES.includes(form.service_type) && <option value={form.service_type}>{form.service_type}</option>}
              </select>
            </div>
            <div><label className="text-xs uppercase text-zinc-400 block mb-1">Status</label>
              <select value={form.status} onChange={set("status")} className={inputCls} data-testid="quote-edit-status">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div><label className="text-xs uppercase text-zinc-400 block mb-1">Vehicle info</label><input value={form.vehicle_info} onChange={set("vehicle_info")} className={inputCls} data-testid="quote-edit-vehicle" /></div>
          <div><label className="text-xs uppercase text-zinc-400 block mb-1">Message</label><textarea rows={3} value={form.message} onChange={set("message")} className={inputCls} data-testid="quote-edit-message" /></div>
          <button onClick={save} disabled={saving} data-testid="quote-edit-save"
            className="w-full bg-yellow-500 text-black font-bold uppercase py-3 hover:bg-yellow-300 transition-colors duration-150 disabled:opacity-60">
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/quote-requests").then(({ data }) => setQuotes(data));
  useEffect(() => { load(); }, []);

  const del = async (q) => {
    try {
      await api.delete(`/quote-requests/${q.id}`);
      toast.success("Quote request deleted");
      setQuotes((prev) => prev.filter((x) => x.id !== q.id));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const onSaved = (updated) => {
    setQuotes((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    setEditing(null);
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-display text-3xl font-black uppercase tracking-tighter">Quote Requests</h1>
        <p className="text-zinc-500 text-sm">Incoming leads from the website — edit details & track status</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {quotes.map((q) => (
          <div key={q.id} className="border border-zinc-800 bg-zinc-950 p-5" data-testid={`quote-${q.id}`}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-display text-lg font-bold">{q.name}</p>
              <div className="flex items-center gap-2">
                <span className={`text-xs uppercase px-2 py-1 font-bold ${STATUS_COLORS[q.status] || STATUS_COLORS.new}`}>{q.status || "new"}</span>
                <button onClick={() => setEditing(q)} data-testid={`quote-edit-${q.id}`} className="text-zinc-400 hover:text-yellow-500 transition-colors duration-150"><Pencil size={16} /></button>
                <button onClick={() => del(q)} data-testid={`quote-delete-${q.id}`} className="text-zinc-400 hover:text-red-500 transition-colors duration-150"><Trash2 size={16} /></button>
              </div>
            </div>
            <p className="text-xs uppercase text-yellow-500 bg-yellow-500/10 px-2 py-1 font-bold inline-block mb-3">{q.service_type}</p>
            <p className="text-zinc-400 text-sm flex items-center gap-2 mb-1"><Mail size={14} /> {q.email}</p>
            {q.phone && <p className="text-zinc-400 text-sm flex items-center gap-2 mb-1"><Phone size={14} /> {q.phone}</p>}
            {q.vehicle_info && <p className="text-zinc-400 text-sm flex items-center gap-2 mb-3"><Truck size={14} /> {q.vehicle_info}</p>}
            {q.message && <p className="text-zinc-300 text-sm border-t border-zinc-800 pt-3">{q.message}</p>}
          </div>
        ))}
        {quotes.length === 0 && <p className="text-zinc-600 col-span-2 text-center py-10">No quote requests yet.</p>}
      </div>
      {editing && <EditModal quote={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
}
